import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { oltAPI } from '../../services/api';
import { LoadingBlock, EmptyState, MeterBar } from '../../components/technical/TechnicalUI';
import { ArrowLeft, Router } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

const fmtBytes = bytes => {
  const gb = (bytes || 0) / 1e9;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${((bytes || 0) / 1e6).toFixed(0)} MB`;
};

function Field({ label, value }) {
  return (
    <div style={{ padding: '9px 0', borderBottom: '1px solid var(--cb-f1f5f9)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: '.85rem', fontWeight: 600, textAlign: 'right' }}>{value ?? '—'}</span>
    </div>
  );
}

export default function TechnicalOltDetailPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [ports, setPorts] = useState([]);
  const [gePorts, setGePorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      oltAPI.getStatus(),
      oltAPI.getPorts().catch(() => ({ data: { data: [] } })),
      oltAPI.getGEPorts().catch(() => ({ data: { data: [] } })),
    ]).then(([s, p, g]) => {
      setStatus(s.data); setPorts(p.data.data || []); setGePorts(g.data.data || []);
    }).catch(() => setStatus({ configured: false })).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock />;
  if (!status?.configured || !status.data) return <EmptyState icon={Router} text="OLT unreachable or not configured" />;

  const d = status.data;
  const totalOnus = ports.reduce((a, p) => a + p.totalOnus, 0);
  const onlineOnus = ports.reduce((a, p) => a + p.onlineOnus, 0);
  const ponPct = totalOnus > 0 ? Math.round((onlineOnus / totalOnus) * 100) : 0;
  const rxTotal = gePorts.reduce((a, p) => a + (p.rxBytes || 0), 0);
  const txTotal = gePorts.reduce((a, p) => a + (p.txBytes || 0), 0);

  return (
    <div>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '.85rem', cursor: 'pointer', padding: 0, marginBottom: 14 }}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="page-header">
        <h2>{d.hostname || 'OLT'}</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>{d.location || status.webURL || ''}</p>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
          <div className="stat-info">
            <p>Uptime</p>
            <h3>{d.uptime || '—'}</h3>
          </div>
        </div>
        <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
          <div className="stat-info">
            <p>Temperature</p>
            <h3>{d.temperature || '—'}</h3>
          </div>
        </div>
      </div>

      <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '16px 18px', marginBottom: 16, background: 'var(--cb-ffffff)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '.85rem' }}>
          <span style={{ fontWeight: 600 }}>CPU Usage</span><span>{d.cpuUsage || '—'}</span>
        </div>
        <MeterBar pct={parseFloat(d.cpuUsage) || 0} severityByPct height={7} />
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '14px 0 6px', fontSize: '.85rem' }}>
          <span style={{ fontWeight: 600 }}>Memory Usage</span><span>{d.memoryUsage || '—'}</span>
        </div>
        <MeterBar pct={parseFloat(d.memoryUsage) || 0} severityByPct height={7} />
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '14px 0 6px', fontSize: '.85rem' }}>
          <span style={{ fontWeight: 600 }}>PON Usage</span><span>{onlineOnus} / {totalOnus}</span>
        </div>
        <MeterBar pct={ponPct} color="var(--primary)" height={7} />
      </div>

      {gePorts.length > 0 && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
            <div className="stat-info">
              <p>Reception</p>
              <h3>↓ {fmtBytes(rxTotal)}</h3>
            </div>
          </div>
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
            <div className="stat-info">
              <p>Transmission</p>
              <h3>↑ {fmtBytes(txTotal)}</h3>
            </div>
          </div>
        </div>
      )}

      <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)' }}>
        <Field label="Model" value={d.model} />
        <Field label="Serial No." value={d.serialNumber} />
        <Field label="Hardware Version" value={d.hardwareVersion} />
        <Field label="Firmware" value={d.softwareVersion} />
        <Field label="MAC Address" value={d.macAddress} />
        <Field label="Startup Time" value={d.startupTime} />
      </div>
    </div>
  );
}
